import React, { useState } from 'react';
import { Casa, Esfuerzo, Tarea, TipoRepeticion } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { usePlus } from '../../context/PlusContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { useLimite } from '../../hooks/useLimite';
import { ContadorLimite } from '../plus/ContadorLimite';
import { EtiquetaPlus } from '../informes/comun';
import { Pause, Play, Repeat, Trash2 } from 'lucide-react';
import {
  BotonesModal, Campo, ICONOS_TAREA, MensajeError, ModalBase, aInput, chip, claseInput, deInput,
} from './ui';
import { describirRepeticion, diaSemana, ESFUERZOS, INICIALES_DIA, NOMBRES_DIA, ZONAS } from '../../utils/tareas';
import { vibrar } from '../../utils/hapticos';
import { avisarAsignacion } from '../../utils/avisarTareas';

interface Props {
  /** Para editar; sin ella, se crea una nueva. */
  tarea?: Tarea;
  casas: Casa[];
  /** Casa elegida al crear. */
  casaId: string;
  /** Día propuesto al crear (el que se mira en la semana). */
  fecha?: Date;
  onClose: () => void;
}

const TIPOS: { tipo: TipoRepeticion; nombre: string }[] = [
  { tipo: 'una', nombre: 'Una vez' },
  { tipo: 'dias', nombre: 'Días' },
  { tipo: 'semanas', nombre: 'Semanas' },
  { tipo: 'meses', nombre: 'Meses' },
];

/** Crear o editar una tarea: qué, dónde, a quién le toca, cada cuánto y cuándo. */
export const ModalTarea: React.FC<Props> = ({ tarea, casas, casaId: casaInicial, fecha, onClose }) => {
  const { crearTarea, actualizarTarea, eliminarTarea, casaConPlus } = useTareas();
  const { ofrecerSiEsLimite, ofrecerPlus } = usePlus();
  const confirmar = useConfirmar();

  const [casaId, setCasaId] = useState(tarea?.casaId ?? casaInicial);
  const casa = casas.find(c => c.id === casaId);
  const limite = useLimite('tareasPeriodicas', casaId);

  const fechaInicial = tarea?.fecha ?? fecha ?? new Date();
  const [nombre, setNombre] = useState(tarea?.nombre ?? '');
  const [icono, setIcono] = useState(tarea?.icono ?? ICONOS_TAREA[0]);
  const [zona, setZona] = useState(tarea?.zona ?? '');
  const [asignadoA, setAsignadoA] = useState(tarea?.asignadoA ?? '');
  // Por turnos: quiénes y en qué orden (el orden en que se tocan)
  const [porTurnos, setPorTurnos] = useState(!!tarea?.turno);
  const [turno, setTurno] = useState<string[]>(tarea?.turno ?? []);
  const [tipo, setTipo] = useState<TipoRepeticion>(tarea?.repeticion.tipo ?? 'semanas');
  const [cada, setCada] = useState(String(tarea?.repeticion.cada ?? 1));
  const [dias, setDias] = useState<number[]>(tarea?.repeticion.diasSemana ?? [diaSemana(fechaInicial)]);
  const [dia, setDia] = useState(aInput(fechaInicial));
  const [hora, setHora] = useState(tarea?.hora ?? '');
  const [esfuerzo, setEsfuerzo] = useState<Esfuerzo>(tarea?.esfuerzo ?? 1);
  const [notas, setNotas] = useState(tarea?.notas ?? '');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cadaNum = Math.max(1, Math.min(99, parseInt(cada, 10) || 1));
  const repeticion = { tipo, cada: cadaNum, ...(tipo === 'semanas' ? { diasSemana: dias } : {}) };
  const fechaElegida = deInput(dia);
  const personas = (casa?.participantes ?? []).filter(p => p.activo || p.id === asignadoA);
  // Al crear una que se repite, o al pasar a repetir una que no lo hacía
  const gastaLimite = tipo !== 'una' && !(tarea && tarea.activa && tarea.repeticion.tipo !== 'una');

  const conPlus = casaConPlus(casaId);
  const elegirTurnos = () => {
    if (!conPlus) { ofrecerPlus('tareasPlus'); return; }
    setPorTurnos(true);
    if (!turno.length) setTurno(asignadoA ? [asignadoA] : []);
  };
  const cambiarEnTurno = (id: string) =>
    setTurno(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const nombreDe = (id: string) => casa?.participantes.find(p => p.id === id)?.nombre ?? '';
  // Con turnos le toca a quien ya le tocaba si sigue en el turno; si no, al primero
  const conTurno = !!casa && !casa.personal && porTurnos && tipo !== 'una';
  const asignadoFinal = casa?.personal ? undefined
    : conTurno ? (turno.includes(asignadoA) ? asignadoA : turno[0]) : asignadoA || undefined;

  const cambiarDia = (d: number) =>
    setDias(prev => (prev.includes(d) ? (prev.length > 1 ? prev.filter(x => x !== d) : prev) : [...prev, d].sort()));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) { setError('Ponle un nombre a la tarea.'); return; }
    if (!fechaElegida) { setError('Elige qué día toca.'); return; }
    if (conTurno && turno.length < 2) { setError('Para ir por turnos, elige al menos a dos personas.'); return; }
    setGuardando(true);
    setError('');
    const datos = {
      nombre: nombre.trim(), icono, zona: zona || undefined,
      asignadoA: asignadoFinal,
      turno: conTurno ? turno : undefined,
      repeticion, fecha: fechaElegida, hora: hora || undefined, esfuerzo, notas: notas.trim() || undefined,
    };
    try {
      const id = tarea ? (await actualizarTarea(tarea, datos), tarea.id) : await crearTarea(casaId, datos);
      // A quien le acaban de asignar, un aviso al móvil (si es otro y la casa tiene Plus)
      if (asignadoFinal && asignadoFinal !== tarea?.asignadoA && !casa?.personal) {
        avisarAsignacion({ casaId, tareaId: id, motivo: 'asignada' });
      }
      vibrar.exito();
      onClose();
    } catch (err) {
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      console.error(err);
      vibrar.error();
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  const pausar = async () => {
    if (!tarea) return;
    try {
      await actualizarTarea(tarea, { activa: !tarea.activa });
      onClose();
    } catch (err) {
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError('No se pudo guardar. Inténtalo de nuevo.');
    }
  };

  const borrar = async () => {
    if (!tarea) return;
    if (!(await confirmar({
      titulo: `¿Borrar «${tarea.nombre}»?`,
      mensaje: 'Lo que ya se hizo se queda en el historial.',
      accion: 'Borrar', destructiva: true,
    }))) return;
    try {
      await eliminarTarea(tarea);
      vibrar.fuerte();
      onClose();
    } catch {
      setError('No se pudo borrar. Solo puede hacerlo quien la creó o quien organiza la casa.');
    }
  };

  return (
    <ModalBase titulo={tarea ? 'Editar tarea' : 'Nueva tarea'} onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />

        {!tarea && casas.length > 1 && (
          <Campo label="Casa">
            <div className="flex flex-wrap gap-1.5">
              {casas.map(c => (
                <button key={c.id} type="button" onClick={() => { setCasaId(c.id); setAsignadoA(''); setTurno([]); setPorTurnos(false); }}
                  className={chip(c.id === casaId)}>
                  {c.icono} {c.nombre}
                </button>
              ))}
            </div>
          </Campo>
        )}

        <Campo label="¿Qué hay que hacer? *">
          <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Sacar la basura, poner la lavadora…" className={claseInput} autoFocus={!tarea} />
        </Campo>

        <Campo label="Icono">
          <div className="flex flex-wrap gap-1.5">
            {ICONOS_TAREA.map(i => (
              <button key={i} type="button" onClick={() => setIcono(i)} aria-pressed={icono === i}
                className={`w-9 h-9 rounded-lg text-lg border transition ${
                  icono === i
                    ? 'border-lumbre-500 bg-lumbre-50 dark:bg-lumbre-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                }`}>
                {i}
              </button>
            ))}
          </div>
        </Campo>

        {casa && !casa.personal && (
          <Campo label="¿A quién le toca?"
            ayuda={conTurno ? 'Toca a las personas en el orden en que les toca. Al hacerla, pasa a la siguiente.'
              : 'Sin nadie elegido, la puede hacer cualquiera de la casa.'}>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => { setAsignadoA(''); setPorTurnos(false); }} className={chip(!asignadoA && !conTurno)}>
                Cualquiera
              </button>
              {!conTurno && personas.map(p => (
                <button key={p.id} type="button" onClick={() => { setAsignadoA(p.id); setPorTurnos(false); }}
                  className={chip(asignadoA === p.id && !conTurno)}>
                  {p.nombre}
                </button>
              ))}
              {tipo !== 'una' && personas.length > 1 && (
                <button type="button" onClick={elegirTurnos} aria-pressed={conTurno}
                  className={`${chip(conTurno)} inline-flex items-center gap-1`}>
                  <Repeat className="w-3.5 h-3.5" /> Por turnos {!conPlus && <EtiquetaPlus />}
                </button>
              )}
            </div>
            {conTurno && (
              <>
                <div className="flex flex-wrap gap-1.5 mt-2" role="group" aria-label="Orden del turno">
                  {personas.map(p => {
                    const pos = turno.indexOf(p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => cambiarEnTurno(p.id)} aria-pressed={pos >= 0}
                        className={`${chip(pos >= 0)} inline-flex items-center gap-1`}>
                        {pos >= 0 && <span className="tabular-nums opacity-80">{pos + 1}.</span>} {p.nombre}
                      </button>
                    );
                  })}
                </div>
                {turno.length >= 2 && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                    Ahora le toca a {nombreDe(asignadoFinal ?? '')}; después, a{' '}
                    {nombreDe(turno[(turno.indexOf(asignadoFinal ?? '') + 1) % turno.length])}.
                  </p>
                )}
              </>
            )}
          </Campo>
        )}

        <Campo label="¿Cada cuánto?">
          <div className="flex flex-wrap gap-1.5">
            {TIPOS.map(t => (
              <button key={t.tipo} type="button" onClick={() => setTipo(t.tipo)} className={chip(tipo === t.tipo)}>
                {t.nombre}
              </button>
            ))}
          </div>
          {tipo !== 'una' && (
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-700 dark:text-gray-300">
              <span>Cada</span>
              <input type="number" inputMode="numeric" min={1} max={99} value={cada}
                onChange={e => setCada(e.target.value)} className={`${claseInput} !w-20`} aria-label="Cada cuántos" />
              <span>{tipo === 'dias' ? (cadaNum === 1 ? 'día' : 'días') : tipo === 'semanas'
                ? (cadaNum === 1 ? 'semana' : 'semanas') : (cadaNum === 1 ? 'mes' : 'meses')}</span>
            </div>
          )}
          {tipo === 'semanas' && (
            <div className="flex gap-1 mt-2" role="group" aria-label="Días de la semana">
              {INICIALES_DIA.map((ini, d) => (
                <button key={d} type="button" onClick={() => cambiarDia(d)} aria-pressed={dias.includes(d)}
                  aria-label={NOMBRES_DIA[d]}
                  className={`w-10 h-10 rounded-full text-sm font-semibold border transition ${
                    dias.includes(d)
                      ? 'bg-lumbre-600 border-lumbre-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                  }`}>
                  {ini}
                </button>
              ))}
            </div>
          )}
          {tipo !== 'una' && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
              {describirRepeticion(repeticion, fechaElegida)}
            </p>
          )}
        </Campo>

        <div className="grid gap-3 grid-cols-2">
          <Campo label={tarea ? 'Próxima vez' : tipo === 'una' ? 'Día' : 'Primera vez'}>
            <input type="date" value={dia} onChange={e => setDia(e.target.value)} className={claseInput} />
          </Campo>
          <Campo label="Hora del aviso">
            <input type="time" value={hora} onChange={e => setHora(e.target.value)} className={claseInput} />
          </Campo>
        </div>

        <Campo label="Zona">
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setZona('')} className={chip(!zona)}>Ninguna</button>
            {ZONAS.map(z => (
              <button key={z} type="button" onClick={() => setZona(z)} className={chip(zona === z)}>{z}</button>
            ))}
          </div>
        </Campo>

        <Campo label="Esfuerzo" ayuda="Sirve para repartir las tareas de forma justa.">
          <div className="flex flex-wrap gap-1.5">
            {ESFUERZOS.map(es => (
              <button key={es.valor} type="button" onClick={() => setEsfuerzo(es.valor)} className={chip(esfuerzo === es.valor)}>
                {es.nombre}
              </button>
            ))}
          </div>
        </Campo>

        <Campo label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
            placeholder="Opcional" className={`${claseInput} resize-y`} />
        </Campo>

        {gastaLimite && limite.texto && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Tareas que se repiten en esta casa <ContadorLimite texto={limite.texto} />
          </p>
        )}

        <BotonesModal guardando={guardando} texto={tarea ? 'Guardar cambios' : 'Crear tarea'} onClose={onClose} />

        {tarea && (
          <div className="flex flex-wrap justify-between gap-2 pt-1">
            <button type="button" onClick={pausar}
              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:underline">
              {tarea.activa ? <><Pause className="w-3.5 h-3.5" /> Pausar (no saldrá en la semana)</>
                : <><Play className="w-3.5 h-3.5" /> Volver a activar</>}
            </button>
            <button type="button" onClick={borrar}
              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline">
              <Trash2 className="w-3.5 h-3.5" /> Borrar tarea
            </button>
          </div>
        )}
      </form>
    </ModalBase>
  );
};
